import React from 'react';

import {
  TextInput,
  Text,
  Anchor,
  Paper,
  Title,
  Container,
  Group,
  Button,
  Center,
  Box,
  createStyles,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconArrowLeft } from '@tabler/icons-react';
import { ReactComponent as KiloLogo } from 'assets/svg/long_kilo_blue_white.svg';
import useResetPassword from 'hooks/auth/useResetPassword';
import { Link } from 'react-router-dom';
import { emailValidator } from 'utils/form-validators';
import notificationHandler from 'utils/notifications';

interface FormValues {
  email: string;
}

const useStyles = createStyles(theme => ({
  title: {
    fontWeight: 900,
  },

  controls: {
    [theme.fn.smallerThan('xs')]: {
      flexDirection: 'column-reverse',
    },
  },

  control: {
    [theme.fn.smallerThan('xs')]: {
      width: '100%',
      textAlign: 'center',
    },
  },
}));

const PasswordReset = (): JSX.Element => {
  const { classes, theme } = useStyles();

  const resetPasswordMutation = useResetPassword();
  const { errorNotification } = notificationHandler();

  const form = useForm<FormValues>({
    initialValues: {
      email: '',
    },
    validate: {
      email: value => emailValidator(value),
    },
  });

  const handleSubmit = (formValues: FormValues): void => {
    resetPasswordMutation.mutate(formValues, {
      onError: error => {
        errorNotification('Reset email failed', error.response?.data.message);
      },
    });
  };

  return (
    <Container size='xs' py='xl'>
      <KiloLogo width='100%' height={60} />
      <Title align='center' mt='xl' className={classes.title}>
        Reset Password
      </Title>

      <Paper withBorder shadow='md' p={30} mt={30} radius='md'>
        {resetPasswordMutation.isSuccess ? (
          <>
            <Text color='dimmed' align='center'>
              If an account matches that email, you&apos;ll receive reset instructions shortly. Check your
              spam folder if you don&apos;t see them.
            </Text>
            <Group position='center' mt='md'>
              <Anchor component={Link} to='/login' size='sm'>
                <Center inline>
                  <IconArrowLeft size={theme.fontSizes.sm} />
                  <Box ml='xs'>Back to login page</Box>
                </Center>
              </Anchor>
            </Group>
          </>
        ) : (
          <>
            <Text color='dimmed'>
              Enter the email you used when you joined and we’ll send you instructions to reset your
              password.
            </Text>
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <TextInput
                label='Email'
                placeholder='jane@mygym.com'
                mt='md'
                readOnly={resetPasswordMutation.isLoading}
                required
                {...form.getInputProps('email')}
              />

              <Group position='apart' mt='md' className={classes.controls}>
                <Anchor component={Link} to='/login' size='sm' color='dimmed' className={classes.control}>
                  <Center inline>
                    <IconArrowLeft size={theme.fontSizes.sm} />
                    <Box ml='xs'>Back to login page</Box>
                  </Center>
                </Anchor>
                <Button type='submit' loading={resetPasswordMutation.isLoading} className={classes.control}>
                  Send Email
                </Button>
              </Group>
            </form>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default PasswordReset;
